import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2, X, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { BodyMap, type BodyAnnotation } from './BodyMap';
import { FRONT_REGIONS, BACK_REGIONS } from './body-map-data';
import { getErrorMessage } from '@/lib/errors';

interface Props { id: string; canEdit: boolean; consultaId?: string | null }

interface MapData {
  genero: 'masculino' | 'femenino';
  anotaciones: BodyAnnotation[];
}

function emptyMap(genero: 'masculino' | 'femenino' = 'masculino'): MapData {
  return { genero, anotaciones: [] };
}

export function ExploracionTopograficaView({ id, canEdit, consultaId }: Props) {
  const qc = useQueryClient();

  const [view, setView]   = useState<'front' | 'back'>('front');
  const [front, setFront] = useState<MapData>(emptyMap());
  const [back,  setBack]  = useState<MapData>(emptyMap());
  const [dirty, setDirty] = useState(false);

  const [selectedId,    setSelectedId]    = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [notaText,      setNotaText]      = useState('');

  // ── Load ──────────────────────────────────────────────────────────────────
  const { data: paciente } = useQuery({
    queryKey: ['paciente', id],
    queryFn: () => api.get(`/api/pacientes/${id}`).then(r => r.data),
  });

  const defaultGenero: 'masculino' | 'femenino' =
    paciente?.genero === 'femenino' ? 'femenino' : 'masculino';

  const { data: savedFront, isLoading: loadingFront } = useQuery({
    queryKey: ['mapa-topografico', id, 'corporal_anterior', consultaId ?? null],
    queryFn: () => api.get(`/api/pacientes/${id}/mapa-topografico`, { params: { lado: 'corporal_anterior', consultaId: consultaId ?? undefined } }).then(r => r.data),
  });
  const { data: savedBack, isLoading: loadingBack } = useQuery({
    queryKey: ['mapa-topografico', id, 'corporal_posterior', consultaId ?? null],
    queryFn: () => api.get(`/api/pacientes/${id}/mapa-topografico`, { params: { lado: 'corporal_posterior', consultaId: consultaId ?? undefined } }).then(r => r.data),
  });

  useEffect(() => {
    if (loadingFront) return;
    if (savedFront?.datosJson) setFront(savedFront.datosJson as MapData);
    else setFront(emptyMap(defaultGenero));
  }, [savedFront, loadingFront, defaultGenero]);

  useEffect(() => {
    if (loadingBack) return;
    if (savedBack?.datosJson) setBack(savedBack.datosJson as MapData);
    else setBack(emptyMap(defaultGenero));
  }, [savedBack, loadingBack, defaultGenero]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([
        api.put(`/api/pacientes/${id}/mapa-topografico`, { lado: 'corporal_anterior', datosJson: front, consultaId: consultaId ?? undefined }),
        api.put(`/api/pacientes/${id}/mapa-topografico`, { lado: 'corporal_posterior', datosJson: back, consultaId: consultaId ?? undefined }),
      ]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapa-topografico', id, 'corporal_anterior', consultaId ?? null] });
      qc.invalidateQueries({ queryKey: ['mapa-topografico', id, 'corporal_posterior', consultaId ?? null] });
      toast.success('Mapa topográfico guardado');
      setDirty(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const current    = view === 'front' ? front : back;
  const setCurrentMap = (fn: (prev: MapData) => MapData) => {
    if (view === 'front') setFront(fn); else setBack(fn);
    setDirty(true);
  };

  function onRegionClick(regionId: string, label: string) {
    setSelectedId(regionId);
    setSelectedLabel(label);
    setNotaText(current.anotaciones.find(a => a.regionId === regionId)?.nota ?? '');
  }

  function saveAnnotation() {
    const nota = notaText.trim();
    setCurrentMap(prev => {
      const filtered = prev.anotaciones.filter(a => a.regionId !== selectedId!);
      return { ...prev, anotaciones: nota ? [...filtered, { regionId: selectedId!, nota }] : filtered };
    });
    setSelectedId(null);
    setNotaText('');
  }

  function removeAnnotation(regionId: string) {
    setCurrentMap(prev => ({ ...prev, anotaciones: prev.anotaciones.filter(a => a.regionId !== regionId) }));
    if (selectedId === regionId) { setSelectedId(null); setNotaText(''); }
  }

  function setGender(g: 'masculino' | 'femenino') {
    setFront(prev => ({ ...prev, genero: g }));
    setBack(prev  => ({ ...prev, genero: g }));
    setDirty(true);
  }

  // Clear selection when switching view (region IDs differ between front/back)
  function switchView(v: 'front' | 'back') {
    setView(v);
    setSelectedId(null);
    setNotaText('');
  }

  const allAnnotations = [
    ...front.anotaciones.map(a => ({
      ...a,
      lado: 'Anterior',
      label: FRONT_REGIONS.find(r => r.id === a.regionId)?.label ?? a.regionId,
    })),
    ...back.anotaciones.map(a => ({
      ...a,
      lado: 'Posterior',
      label: BACK_REGIONS.find(r => r.id === a.regionId)?.label ?? a.regionId,
    })),
  ];
  const isLoading = loadingFront || loadingBack;

  if (isLoading) return <p className="text-center py-8 text-sm text-text-secondary">Cargando...</p>;

  return (
    <div className="space-y-4">

      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">

          {/* Género */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            <button
              onClick={() => setGender('masculino')} disabled={!canEdit}
              className={cn('px-3 py-1.5 transition-colors',
                front.genero === 'masculino' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              ♂ Masculino
            </button>
            <button
              onClick={() => setGender('femenino')} disabled={!canEdit}
              className={cn('px-3 py-1.5 transition-colors',
                front.genero === 'femenino' ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              ♀ Femenino
            </button>
          </div>

          {/* Vista anterior / posterior */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            <button
              onClick={() => switchView('front')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 transition-colors',
                view === 'front' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              <User size={11} />
              Anterior
              {front.anotaciones.length > 0 && (
                <span className={cn('text-[9px] rounded-full px-1 font-bold leading-none',
                  view === 'front' ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600')}>
                  {front.anotaciones.length}
                </span>
              )}
            </button>
            <button
              onClick={() => switchView('back')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 transition-colors',
                view === 'back' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
              <User size={11} className="scale-x-[-1]" />
              Posterior
              {back.anotaciones.length > 0 && (
                <span className={cn('text-[9px] rounded-full px-1 font-bold leading-none',
                  view === 'back' ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600')}>
                  {back.anotaciones.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {canEdit && dirty && (
          <button
            onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            <Save size={12} /> {saveMutation.isPending ? 'Guardando...' : 'Guardar mapa'}
          </button>
        )}
      </div>

      {/* ── Main grid ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.6fr] gap-4">

        {/* Mapa corporal */}
        <div className="bg-white border border-gray-100 rounded-2xl p-3">
          <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-2 text-center">
            Vista {view === 'front' ? 'Anterior' : 'Posterior'} — {current.anotaciones.length} nota{current.anotaciones.length !== 1 ? 's' : ''}
          </p>
          <BodyMap
            view={view}
            gender={current.genero}
            annotations={current.anotaciones}
            selectedRegion={selectedId}
            onRegionClick={onRegionClick}
            canEdit={canEdit}
          />
        </div>

        {/* Panel de anotaciones */}
        <div className="space-y-3">

          {/* Editor */}
          {selectedId && canEdit ? (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-blue-800">{selectedLabel}</p>
                <button onClick={() => { setSelectedId(null); setNotaText(''); }}
                  className="text-blue-400 hover:text-blue-600 transition-colors">
                  <X size={14} />
                </button>
              </div>
              <textarea
                autoFocus
                rows={3}
                value={notaText}
                onChange={e => setNotaText(e.target.value)}
                placeholder="Hallazgos, descripciones, signos clínicos..."
                className="w-full text-xs resize-none bg-white border border-blue-200 rounded-lg px-2.5 py-2 outline-none focus:ring-2 focus:ring-blue-300"
              />
              <div className="flex gap-2 justify-end">
                {current.anotaciones.some(a => a.regionId === selectedId) && (
                  <button onClick={() => removeAnnotation(selectedId)}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded transition-colors">
                    <Trash2 size={11} /> Eliminar
                  </button>
                )}
                <button onClick={saveAnnotation}
                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                  {notaText.trim() ? 'Guardar nota' : 'Cerrar'}
                </button>
              </div>
            </div>
          ) : canEdit ? (
            <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-4 text-center text-xs text-text-secondary">
              Selecciona una región en el mapa para agregar una nota
            </div>
          ) : null}

          {/* Lista de notas (ambas vistas) */}
          {allAnnotations.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-3 space-y-2 max-h-72 overflow-y-auto">
              <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">
                Notas registradas ({allAnnotations.length})
              </p>
              {allAnnotations.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                  <span className="text-[10px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 flex-shrink-0 mt-0.5">
                    {a.lado}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary">{a.label}</p>
                    <p className="text-text-secondary mt-0.5 line-clamp-2">{a.nota}</p>
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => {
                        // Switch to the right view before removing
                        const targetView = a.lado === 'Anterior' ? 'front' : 'back';
                        if (view !== targetView) switchView(targetView);
                        // Delay one tick so state updates
                        setTimeout(() => removeAnnotation(a.regionId), 0);
                      }}
                      className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {allAnnotations.length === 0 && !selectedId && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center text-xs text-text-secondary space-y-1">
              <User size={24} className="mx-auto text-gray-300 mb-2" />
              <p className="font-medium">Sin hallazgos topográficos</p>
              <p>Selecciona una región del mapa para agregar notas</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
